import type { PropsWithChildren } from "react";
import { Routes, Route, Navigate, useLocation, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useCurrentUser } from "../api/auth";
import { useUserRoles } from "../api/users";
import { AdminDashboard } from "./AdminDashboard";
import { CampaignsPanel } from "./CampaignsPanel";
import { DistributorsPanel } from "./DistributorsPanel";
import { UsersPanel } from "./UsersPanel";
import { AdminRegistrationsPanel } from "./AdminRegistrationsPanel";
import { DeliveryReportsPage } from "./DeliveryReportsPage";
import { BulkDistributionPanel } from "./BulkDistributionPanel";
import { HiddenAddressesPanel } from "./HiddenAddressesPanel";
import { UnderDeliveredAddressesPanel } from "./UnderDeliveredAddressesPanel";
import { DuplicateAddressCandidatesPage } from "./DuplicateAddressCandidatesPage";
import { ActivityReportsPage } from "./ActivityReportsPage";
import { ImportsPanel } from "./ImportsPanel";
import { HangfireJobsPanel } from "./HangfireJobsPanel";
import { AuditDashboard } from "./AuditDashboard";
import { SecurityDashboard } from "./SecurityDashboard";
import { AdminAssignmentsPage } from "./AdminAssignmentsPage";
import { AdminAssignmentsTablePage } from "./AdminAssignmentsTablePage";
import { KretsMembersPanel } from "./KretsMembersPanel";
import { RoReportingPage } from "./RoReportingPage";
import { MetadataFieldsPage } from "./MetadataFieldsPage";
import { MetadataGridPage } from "./MetadataGridPage";
import { getHangfireUrl } from "../api/hangfire";
import { useImportsAccess } from "../api/imports";
import { useElectoralMetadataFieldsAccess } from "../api/electoral-metadata/hooks";
import { WwwPortalNavBar } from "../app/WwwPortalNavBar";
import "./admin-layout.css";

const ImportsRouteGuard = ({ children }: PropsWithChildren): JSX.Element => {
    const { data: importsAccess, isSuccess } = useImportsAccess();
    if (isSuccess && importsAccess === false) {
        return <Navigate to="/admin" replace />;
    }
    return <>{children}</>;
};

const MetadataFieldsRouteGuard = ({ children }: PropsWithChildren): JSX.Element => {
    const { data: fieldsAccess, isSuccess } = useElectoralMetadataFieldsAccess();
    if (isSuccess && fieldsAccess === false) {
        return <Navigate to="/admin/metadata-grid" replace />;
    }
    return <>{children}</>;
};

const SuperAdminRouteGuard = ({ children }: PropsWithChildren): JSX.Element => {
    const { data: currentUser } = useCurrentUser();
    const { data: roles = [], isSuccess } = useUserRoles(currentUser?.id ?? "");
    const isSuperAdmin = isSuccess && roles.some((r) => r.role === "SuperAdmin");
    if (isSuccess && !isSuperAdmin) return <Navigate to="/admin" replace />;
    return <>{children}</>;
};

export const AdminLayout = (): JSX.Element => {
    const location = useLocation();
    const { data: currentUser } = useCurrentUser();
    const { data: currentUserRoles = [] } = useUserRoles(currentUser?.id ?? "");
    const isSuperAdmin = currentUserRoles.some((r) => r.role === "SuperAdmin");

    // Fetch Hangfire URL - only shown if user has access
    const { data: hangfireUrl, isError: hangfireError } = useQuery({
        queryKey: ["hangfire", "url"],
        queryFn: getHangfireUrl,
        retry: false,
        staleTime: 5 * 60 * 1000 // 5 minutes
    });

    const isActive = (path: string): boolean => {
        if (path === "/admin") {
            return location.pathname === "/admin" || location.pathname === "/admin/";
        }
        return location.pathname.startsWith(path);
    };

    // Only show Hangfire Jobs link if user has access to Hangfire
    const showHangfireJobs = hangfireUrl && !hangfireError;

    // Only show Imports link if user is SuperAdmin
    const { data: importsAccess } = useImportsAccess();
    const showImports = importsAccess === true;

    const handleHangfireClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
        if (hangfireUrl) {
            e.preventDefault();
            window.open(hangfireUrl, "_blank", "noopener,noreferrer");
        }
    };

    // Only the map view fills the viewport; the printable table keeps normal page padding.
    const isFullBleed =
        location.pathname.startsWith("/admin/assignments") &&
        !location.pathname.startsWith("/admin/assignments/table");

    return (
        <div className="admin-layout-root">
            <div className="admin-layout__portal-nav">
                <WwwPortalNavBar surface="www_other" />
            </div>
            <div className="admin-layout">
            <nav className="admin-layout__nav" role="navigation" aria-label="Admin navigation">
                <Link
                    to="/"
                    className="admin-layout__nav-link admin-layout__nav-link--back"
                    title="Tillbaka till karta"
                >
                    ← Karta
                </Link>
                <Link
                    to="/admin"
                    className={`admin-layout__nav-link ${isActive("/admin") && !isActive("/admin/campaigns") && !isActive("/admin/users") && !isActive("/admin/distributors") && !isActive("/admin/assignments") && !isActive("/admin/krets-members") && !isActive("/admin/flyer-types") && !isActive("/admin/reports") && !isActive("/admin/bulk-distribution") && !isActive("/admin/hidden-addresses") && !isActive("/admin/under-delivered") && !isActive("/admin/duplicate-candidates") && !isActive("/admin/activities") && !isActive("/admin/registrations") && !isActive("/admin/imports") && !isActive("/admin/audit") && !isActive("/admin/security") && !isActive("/admin/metadata-fields") && !isActive("/admin/metadata-grid") ? "active" : ""}`}
                >
                    KPIs
                </Link>
                <Link
                    to="/admin/metadata-grid"
                    className={`admin-layout__nav-link ${isActive("/admin/metadata-grid") || isActive("/admin/metadata-fields") ? "active" : ""}`}
                >
                    Valdistriktsmetadata
                </Link>
                <Link
                    to="/admin/campaigns"
                    className={`admin-layout__nav-link ${isActive("/admin/campaigns") ? "active" : ""}`}
                >
                    Kampanjer
                </Link>
                <Link
                    to="/admin/users"
                    className={`admin-layout__nav-link ${isActive("/admin/users") ? "active" : ""}`}
                >
                    Användare
                </Link>
                <Link
                    to="/admin/distributors"
                    className={`admin-layout__nav-link ${isActive("/admin/distributors") ? "active" : ""}`}
                >
                    Distributörer
                </Link>
                <Link
                    to="/admin/krets-members"
                    className={`admin-layout__nav-link ${isActive("/admin/krets-members") ? "active" : ""}`}
                >
                    Utdelare
                </Link>
                <Link
                    to="/admin/assignments"
                    className={`admin-layout__nav-link ${isActive("/admin/assignments") ? "active" : ""}`}
                >
                    Uppdrag
                </Link>
                <Link
                    to="/admin/reports"
                    className={`admin-layout__nav-link ${isActive("/admin/reports") ? "active" : ""}`}
                >
                    Utdelningar
                </Link>
                <Link
                    to="/admin/bulk-distribution"
                    className={`admin-layout__nav-link ${isActive("/admin/bulk-distribution") ? "active" : ""}`}
                >
                    Massregistrering
                </Link>
                <Link
                    to="/admin/hidden-addresses"
                    className={`admin-layout__nav-link ${isActive("/admin/hidden-addresses") ? "active" : ""}`}
                >
                    Dolda adresser
                </Link>
                <Link
                    to="/admin/under-delivered"
                    className={`admin-layout__nav-link ${isActive("/admin/under-delivered") ? "active" : ""}`}
                >
                    Underleveranser
                </Link>
                <Link
                    to="/admin/duplicate-candidates"
                    className={`admin-layout__nav-link ${isActive("/admin/duplicate-candidates") ? "active" : ""}`}
                >
                    Adressdubbletter
                </Link>
                <Link
                    to="/admin/ro-reporting"
                    className={`admin-layout__nav-link ${isActive("/admin/ro-reporting") ? "active" : ""}`}
                >
                    RO-rapportering
                </Link>
                <Link
                    to="/admin/activities"
                    className={`admin-layout__nav-link ${isActive("/admin/activities") ? "active" : ""}`}
                >
                    Aktiviteter
                </Link>
                <Link
                    to="/admin/registrations"
                    className={`admin-layout__nav-link ${isActive("/admin/registrations") ? "active" : ""}`}
                >
                    Registreringar
                </Link>
                {showImports && (
                    <Link
                        to="/admin/imports"
                        className={`admin-layout__nav-link ${isActive("/admin/imports") ? "active" : ""}`}
                    >
                        Importer
                    </Link>
                )}
                {isSuperAdmin && (
                    <>
                        <Link
                            to="/admin/audit"
                            className={`admin-layout__nav-link ${isActive("/admin/audit") ? "active" : ""}`}
                        >
                            Audit Log
                        </Link>
                        <Link
                            to="/admin/security"
                            className={`admin-layout__nav-link ${isActive("/admin/security") ? "active" : ""}`}
                        >
                            Security
                        </Link>
                    </>
                )}
                {showHangfireJobs && (
                    <>
                        <Link
                            to="/admin/hangfire-jobs"
                            className={`admin-layout__nav-link ${isActive("/admin/hangfire-jobs") ? "active" : ""}`}
                        >
                            Hangfire-jobb
                        </Link>
                        <a
                            href={hangfireUrl}
                            onClick={handleHangfireClick}
                            className="admin-layout__nav-link"
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Öppna Hangfire Dashboard i ny flik"
                        >
                            Hangfire Dashboard
                        </a>
                    </>
                )}
            </nav>

            <main className={`admin-layout__content${isFullBleed ? " admin-layout__content--full-bleed" : ""}`}>
                <Routes>
                    <Route index element={<AdminDashboard />} />
                    <Route path="campaigns" element={<CampaignsPanel />} />
                    <Route path="users" element={<UsersPanel />} />
                    <Route path="distributors" element={<DistributorsPanel />} />
                    <Route path="assignments" element={<AdminAssignmentsPage />} />
                    <Route path="assignments/table" element={<AdminAssignmentsTablePage />} />
                    <Route path="krets-members" element={<KretsMembersPanel />} />
                    <Route path="reports" element={<DeliveryReportsPage />} />
                    <Route path="bulk-distribution" element={<BulkDistributionPanel />} />
                    <Route path="hidden-addresses" element={<HiddenAddressesPanel />} />
                    <Route path="under-delivered" element={<UnderDeliveredAddressesPanel />} />
                    <Route path="duplicate-candidates" element={<DuplicateAddressCandidatesPage />} />
                    <Route path="ro-reporting" element={<RoReportingPage />} />
                    <Route
                        path="metadata-fields"
                        element={
                            <MetadataFieldsRouteGuard>
                                <MetadataFieldsPage />
                            </MetadataFieldsRouteGuard>
                        }
                    />
                    <Route path="metadata-grid" element={<MetadataGridPage />} />
                    <Route path="activities" element={<ActivityReportsPage />} />
                    <Route path="registrations" element={<AdminRegistrationsPanel />} />
                    <Route
                        path="imports"
                        element={
                            <ImportsRouteGuard>
                                <ImportsPanel />
                            </ImportsRouteGuard>
                        }
                    />
                    <Route
                        path="audit"
                        element={
                            <SuperAdminRouteGuard>
                                <AuditDashboard />
                            </SuperAdminRouteGuard>
                        }
                    />
                    <Route
                        path="security"
                        element={
                            <SuperAdminRouteGuard>
                                <SecurityDashboard />
                            </SuperAdminRouteGuard>
                        }
                    />
                    <Route path="hangfire-jobs" element={<HangfireJobsPanel />} />
                    <Route path="*" element={<Navigate to="/admin" replace />} />
                </Routes>
            </main>
        </div>
        </div>
    );
};